import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaHistory, FaArrowLeft, FaChevronDown, FaChevronRight, FaFileAlt, FaUserCheck, FaUserTimes, FaPlus, FaEdit, FaTrash, FaCheckCircle, FaTimesCircle, FaExchangeAlt } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface ProjectHistoryEntry {
  id: string;
  projectId: string;
  businessId: string;
  businessName: string;
  action: string;
  data: any;
  timestamp: string;
  createdAt: string;
  performedBy: {
    uid: string;
    email: string;
    role: string;
  };
}

const ProjectHistory: React.FC = () => {
  const { projectId } = useParams();
  const { userData } = useAuth();
  const { t } = useTranslation();
  const [history, setHistory] = useState<ProjectHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [clientNames, setClientNames] = useState<{ [clientId: string]: string }>({});
  const [loadingClients, setLoadingClients] = useState(false);
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  useEffect(() => {
    if (projectId) {
      fetchProjectHistory();
    }
    // eslint-disable-next-line
  }, [projectId]);

  // Buscar nombres de clientes únicos en el historial
  useEffect(() => {
    // Extraer todos los clientId únicos de los registros del historial
    const clientIds = Array.from(new Set(
      history
        .filter(h => h.action === 'project_created' && h.data && h.data.clientId)
        .map(h => h.data.clientId)
    ));
    // Buscar solo los que no están en cache
    const idsToFetch = clientIds.filter(id => id && !clientNames[id]);
    if (!userData?.businessId || idsToFetch.length === 0) return;
    setLoadingClients(true);
    const fetchAll = async () => {
      const clientRef = collection(db, `clients/${userData.businessId}/clients`);
      const snap = await getDocs(clientRef);
      const newNames: { [clientId: string]: string } = {};
      idsToFetch.forEach(id => {
        const doc = snap.docs.find(d => d.id === id);
        newNames[id] = doc ? doc.data().name || id : id;
      });
      setClientNames(prev => ({ ...prev, ...newNames }));
      setLoadingClients(false);
    };
    fetchAll();
    // eslint-disable-next-line
  }, [history, userData?.businessId]);

  const fetchProjectHistory = async () => {
    try {
      setLoading(true);
      if (!projectId || !userData || !userData.businessId) {
        console.error(t('projects.history.errorMissingData', 'Missing user or project data'));
        throw new Error(t('projects.history.errorMissingData', 'Missing user or project data'));
      }
      const q = query(
        collection(db, `projects/${userData.businessId}/projects/${projectId}/history`),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const entries: ProjectHistoryEntry[] = [];
      querySnapshot.forEach((doc) => {
        entries.push({ id: doc.id, ...doc.data() } as ProjectHistoryEntry);
      });
      setHistory(entries);
    } catch (error) {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const actionTypes = Array.from(new Set(history.map(h => h.action)));
  const filteredHistory = filter === 'all' ? history : history.filter(h => h.action === filter);

  const toggleAccordion = (idx: number) => {
    setOpenIndexes(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // Función para obtener un resumen breve de cada registro
  const getHistorySummary = (entry: ProjectHistoryEntry) => {
    const { action, data } = entry;
    if (action === 'project_created') return t('projects.history.summary.project_created', 'Project created');
    if (action === 'project_updated') {
      if (data.field === 'documents' && data.action === 'add') return t('projects.history.summary.document_uploaded', 'Document uploaded');
      if (data.field === 'materials' && data.action === 'add') return t('projects.history.summary.material_added', 'Material added');
      if (data.field === 'materials' && data.action === 'delete') return t('projects.history.summary.material_deleted', 'Material deleted');
      if (data.field === 'materials' && data.action === 'edit') return t('projects.history.summary.material_edited', 'Material edited');
      if (data.field === 'name') return t('projects.history.summary.name_changed', 'Project name changed');
      if (data.field === 'description') return t('projects.history.summary.description_changed', 'Description changed');
      if (data.field === 'client') return t('projects.history.summary.client_changed', 'Client changed');
      if (data.field === 'columns') return t('projects.history.summary.columns_changed', 'Columns changed');
      if (data.field === 'documents' && data.action === 'delete') return t('projects.history.summary.document_deleted', 'Document deleted');
      return t('projects.history.summary.project_updated', 'Project updated');
    }
    if (action === 'task_created') return t('projects.history.summary.task_created', 'Task created');
    if (action === 'task_deleted') return t('projects.history.summary.task_deleted', 'Task deleted');
    if (action === 'worker_assigned') return t('projects.history.summary.worker_assigned', 'Worker assigned');
    if (action === 'worker_removed') return t('projects.history.summary.worker_removed', 'Worker removed');
    if (action === 'task_updated') return t('projects.history.summary.task_updated', 'Task updated');
    if (action === 'location_updated') return t('projects.history.summary.location_updated', 'Location updated');
    if (action === 'checklist_updated') return t('projects.history.summary.checklist_updated', 'Checklist updated');
    if (action === 'details_updated') return t('projects.history.summary.details_updated', 'Details updated');
    if (action === 'worker_updated') return t('projects.history.summary.worker_updated', 'Worker updated');
    if (action === 'offer_sent') return t('projects.history.summary.offer_sent', 'Offer sent');
    if (action === 'offer_accepted') return t('projects.history.summary.offer_accepted', 'Offer accepted');
    if (action === 'offer_rejected') return t('projects.history.summary.offer_rejected', 'Offer rejected');
    if (action === 'counter_offer') return t('projects.history.summary.counter_offer', 'Counter offer');
    return t('projects.history.summary.generic', 'Project activity');
  };

  // Función para mostrar resumen legible de cada registro
  const renderHistoryData = (entry: ProjectHistoryEntry) => {
    const { data, action } = entry;
    if (!data) return null;
    // Project created
    if (action === 'project_created') {
      return (
        <div className="space-y-1">
          <div><b>{t('projects.info.name', 'Name')}:</b> {data.name}</div>
          <div><b>{t('projects.client.name', 'Client')}:</b> {clientNames[data.clientId] || data.clientId} {loadingClients && !clientNames[data.clientId] && <span className="text-xs text-gray-400 ml-2">{t('common.loading', 'Loading...')}</span>}</div>
          <div><b>{t('projects.info.startDate', 'Start Date')}:</b> {data.startDate?.seconds ? new Date(data.startDate.seconds * 1000).toLocaleDateString() : '-'}</div>
          <div><b>{t('projects.info.endDate', 'End Date')}:</b> {data.endDate?.seconds ? new Date(data.endDate.seconds * 1000).toLocaleDateString() : '-'}</div>
          <div><b>{t('projects.info.amount', 'Amount')}:</b> ${data.totalAmount || data.total || '-'}</div>
          <div><b>{t('projects.info.progress', 'Progress')}:</b> {data.progress != null ? `${data.progress}%` : '-'}</div>
          <div><b>{t('projects.info.description', 'Description')}:</b> {data.description || '-'}</div>
          {Array.isArray(data.services) && data.services.length > 0 && (
            <div>
              <b>{t('projects.info.services', 'Services')}:</b>
              <ul className="list-disc ml-6">
                {data.services.map((srv: any) => (
                  <li key={srv.id || srv.name}>{srv.name} ({srv.priceType === 'fixed' ? `$${srv.price}` : `$${srv.price} x ${srv.quantity}`})</li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(data.quotationPDF) && data.quotationPDF.length > 0 && (
            <div>
              <b>{t('projects.info.quotation', 'Quotation')}:</b> <a href={data.quotationPDF[0]} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{t('projects.documents.download', 'Download')}</a>
            </div>
          )}
        </div>
      );
    }
    // Documentos
    if (data.field === 'documents' && Array.isArray(data.documents)) {
      return (
        <div>
          <div className="font-semibold mb-1">{t('projects.history.documentsChanged', 'Documents changed')}:</div>
          <ul className="list-disc ml-6">
            {data.documents.map((doc: any) => (
              <li key={doc.id || doc.url} className="mb-1">
                <span className="font-medium">{doc.name}</span> ({(doc.size/1024/1024).toFixed(2)} MB)
                {doc.uploadDate && (
                  <span className="ml-2 text-xs text-gray-500">{new Date(doc.uploadDate).toLocaleDateString()}</span>
                )}
                {doc.url && (
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 underline">{t('projects.documents.download', 'Download')}</a>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    // Materiales
    if (data.field === 'materials' && data.material) {
      const mat = data.material;
      return (
        <div>
          <div className="font-semibold mb-1">{t('projects.history.materialChanged', 'Material changed')}:</div>
          <div><b>{t('projects.materials.name', 'Name')}:</b> {mat.name}</div>
          <div><b>{t('projects.materials.quantity', 'Quantity')}:</b> {mat.quantity} {mat.unit}</div>
          <div><b>{t('projects.materials.price', 'Price')}:</b> ${mat.price}</div>
        </div>
      );
    }
    // Cambios generales de campo
    if (data.field && data.newValue !== undefined) {
      return (
        <div>
          <b>{t('projects.history.fieldChanged', 'Field changed')}:</b> {String(t(`projects.info.${data.field}`, data.field))}<br/>
          <b>{t('projects.history.newValue', 'New value')}:</b> {String(data.newValue)}
        </div>
      );
    }
    // Mostrar cambios específicos con formato old/new
    if (Object.keys(data).some(key => data[key]?.old !== undefined || data[key]?.new !== undefined)) {
      return (
        <div className="space-y-2">
          <div className="font-semibold text-sm text-gray-700 mb-2">
            {t('projects.history.changes', 'Cambios realizados')}:
          </div>
          {Object.entries(data).map(([key, value]: [string, any]) => {
            if (value && typeof value === 'object' && ('old' in value || 'new' in value)) {
              return (
                <div key={key} className="bg-gray-50 rounded p-2 border-l-4 border-blue-500">
                  <div className="font-medium text-sm text-gray-700 capitalize mb-1">{key}:</div>
                  {value.old !== undefined && (
                    <div className="text-xs ml-2">
                      <span className="text-red-600 font-medium">Antes:</span>{' '}
                      <span className="text-gray-700">{typeof value.old === 'object' ? JSON.stringify(value.old) : String(value.old) || '(vacío)'}</span>
                    </div>
                  )}
                  {value.new !== undefined && (
                    <div className="text-xs ml-2">
                      <span className="text-green-600 font-medium">Después:</span>{' '}
                      <span className="text-gray-700">{typeof value.new === 'object' ? JSON.stringify(value.new) : String(value.new) || '(vacío)'}</span>
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }
    // Acción genérica - Mostrar datos estructurados de forma legible
    return (
      <div className="space-y-2">
        <div className="font-semibold text-sm text-gray-700 mb-2">
          {t('projects.history.changeDetails', 'Detalles del cambio')}:
        </div>
        {renderStructuredData(data)}
      </div>
    );
  };

  // Función auxiliar para renderizar datos estructurados de forma más legible
  const renderStructuredData = (data: any, level: number = 0): React.ReactNode => {
    if (!data || typeof data !== 'object') {
      return <span className="text-gray-700">{String(data)}</span>;
    }

    const indent = level * 16;

    return (
      <div className="space-y-1" style={{ marginLeft: `${indent}px` }}>
        {Object.entries(data).map(([key, value]) => {
          // Ignorar campos técnicos poco relevantes
          if (['type', 'nanoseconds'].includes(key)) return null;
          
          // Formatear fechas Firestore
          if (key === 'seconds' && typeof value === 'number') {
            return (
              <div key={key} className="text-sm">
                <span className="font-medium text-gray-600">📅 Fecha:</span>{' '}
                <span className="text-gray-800">{new Date(value * 1000).toLocaleString()}</span>
              </div>
            );
          }

          // Formatear ubicaciones
          if (key === 'location' && typeof value === 'object' && value !== null) {
            const loc = value as any;
            return (
              <div key={key} className="bg-blue-50 rounded p-2 text-sm">
                <div className="font-medium text-blue-700 mb-1">📍 Ubicación:</div>
                {loc.address && <div className="ml-2">• {loc.address}</div>}
                {loc.city && <div className="ml-2">• {loc.city}, {loc.state}</div>}
                {loc.latitude && <div className="ml-2 text-xs text-gray-600">Coordenadas: {loc.latitude}, {loc.longitude}</div>}
              </div>
            );
          }

          // Formatear checklist
          if (key === 'checklist' && Array.isArray(value)) {
            return (
              <div key={key} className="bg-green-50 rounded p-2 text-sm">
                <div className="font-medium text-green-700 mb-1">✓ Checklist ({value.length} items):</div>
                <ul className="ml-2 space-y-1">
                  {value.map((item: any, idx: number) => (
                    <li key={idx} className="flex items-center gap-2">
                      {item.completed ? '✅' : '⬜'} {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          // Formatear tareas (task)
          if (key === 'task' && typeof value === 'object') {
            const task = value as any;
            return (
              <div key={key} className="bg-purple-50 rounded p-2 text-sm space-y-1">
                <div className="font-medium text-purple-700 mb-1">📋 Tarea:</div>
                {task.title && <div className="ml-2"><b>Título:</b> {task.title}</div>}
                {task.description && <div className="ml-2"><b>Descripción:</b> {task.description}</div>}
                {task.progress != null && <div className="ml-2"><b>Progreso:</b> {task.progress}%</div>}
                {task.columnId && <div className="ml-2"><b>Estado:</b> {task.columnId}</div>}
                {task.duration && <div className="ml-2"><b>Duración:</b> {task.duration} {task.durationUnit || 'horas'}</div>}
                {task.isScanningJob && <div className="ml-2 text-orange-600">🔍 Trabajo de escaneo</div>}
                {task.location && renderStructuredData({ location: task.location }, level + 1)}
                {task.checklist && renderStructuredData({ checklist: task.checklist }, level + 1)}
              </div>
            );
          }

          // Formatear worker
          if (key === 'worker' && typeof value === 'object' && value !== null) {
            const worker = value as any;
            return (
              <div key={key} className="bg-yellow-50 rounded p-2 text-sm">
                <div className="font-medium text-yellow-700 mb-1">👤 Trabajador:</div>
                {worker.name && <div className="ml-2">{worker.name}</div>}
                {worker.email && <div className="ml-2">{worker.email}</div>}
              </div>
            );
          }

          // Formatear arrays simples
          if (Array.isArray(value)) {
            if (value.length === 0) return null;
            return (
              <div key={key} className="text-sm">
                <span className="font-medium text-gray-600 capitalize">{key}:</span>
                <ul className="ml-4 mt-1 space-y-1">
                  {value.map((item, idx) => (
                    <li key={idx} className="text-gray-700">
                      {typeof item === 'object' ? renderStructuredData(item, level + 1) : `• ${item}`}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          // Formatear objetos anidados (excepto los ya manejados)
          if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="text-sm">
                <span className="font-medium text-gray-600 capitalize">{key}:</span>
                <div className="ml-2 mt-1 border-l-2 border-gray-200 pl-2">
                  {renderStructuredData(value, level + 1)}
                </div>
              </div>
            );
          }

          // Formatear valores primitivos
          return (
            <div key={key} className="text-sm">
              <span className="font-medium text-gray-600 capitalize">{key}:</span>{' '}
              <span className="text-gray-700">{String(value)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'project_created': return <FaPlus className="text-green-500" />;
      case 'project_updated': return <FaEdit className="text-blue-500" />;
      case 'task_created': return <FaPlus className="text-green-500" />;
      case 'task_deleted': return <FaTrash className="text-red-500" />;
      case 'task_updated': return <FaEdit className="text-blue-400" />;
      case 'worker_assigned': return <FaUserCheck className="text-green-600" />;
      case 'worker_removed': return <FaUserTimes className="text-red-600" />;
      case 'worker_updated': return <FaEdit className="text-blue-600" />;
      case 'location_updated': return <FaEdit className="text-purple-500" />;
      case 'checklist_updated': return <FaEdit className="text-indigo-500" />;
      case 'details_updated': return <FaEdit className="text-cyan-500" />;
      case 'offer_sent': return <FaExchangeAlt className="text-yellow-500" />;
      case 'offer_accepted': return <FaCheckCircle className="text-green-500" />;
      case 'offer_rejected': return <FaTimesCircle className="text-red-500" />;
      case 'counter_offer': return <FaExchangeAlt className="text-purple-500" />;
      default: return <FaFileAlt className="text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header sticky glassmorphism */}
        <div className="sticky top-0 z-20 bg-white/60 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 mb-8 px-8 py-6 flex items-center justify-between gap-4 animate-fadeIn">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 text-gray-600 hover:text-red-600 bg-white/70 rounded-full shadow border border-gray-200 transition-colors"
              title={t('common.back', 'Back')}
            >
              <FaArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-3 tracking-tight">
                <FaHistory className="text-red-500" /> {t('projects.history.title', 'Project History')}
              </h1>
              <p className="text-gray-700 mt-1 text-lg font-semibold">
                {t('projects.history.project', 'Project')}: {
                  (() => {
                    const created = history.find(h => h.action === 'project_created' && h.data && h.data.name);
                    return created ? created.data.name : '-';
                  })()
                }
              </p>
            </div>
          </div>
        </div>
        {/* Filtros sticky glassmorphism */}
        <div className="sticky top-[110px] z-10 bg-white/70 backdrop-blur-xl rounded-xl shadow border border-white/30 mb-4 px-8 py-3 flex flex-wrap gap-2 items-center animate-fadeIn">
          <span className="text-gray-700 font-medium mr-2">{t('projects.history.filter', 'Filtrar por acción:')}</span>
          <button
            className={`px-3 py-1 rounded-full text-sm font-semibold border transition-colors shadow-sm ${filter === 'all' ? 'bg-red-600 text-white border-red-600' : 'bg-white/80 text-gray-700 border-gray-300 hover:bg-red-50'}`}
            onClick={() => setFilter('all')}
          >
            {t('projects.history.all', 'Todas')}
          </button>
          {actionTypes.map(type => (
            <button
              key={type}
              className={`px-3 py-1 rounded-full text-sm font-semibold border transition-colors shadow-sm ${filter === type ? 'bg-red-600 text-white border-red-600' : 'bg-white/80 text-gray-700 border-gray-300 hover:bg-red-50'}`}
              onClick={() => setFilter(type)}
            >
              {t(`projects.history.${type}`, type)}
            </button>
          ))}
        </div>
        {/* Accordion visual mejorado */}
        <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 overflow-hidden animate-fadeIn">
          <div className="px-8 py-6 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 tracking-wide flex items-center gap-2">
              <FaFileAlt className="text-red-400" /> {t('projects.history.records', 'Records')} ({filteredHistory.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredHistory.map((entry, idx) => {
              const isOpen = openIndexes.includes(idx);
              return (
                <div key={entry.id} className={`transition-all duration-300 ${isOpen ? 'bg-white/90' : 'bg-white/60 hover:bg-red-50'}` }>
                  <button
                    className={`w-full flex items-center justify-between px-8 py-5 transition-colors focus:outline-none group ${isOpen ? 'border-l-4 border-red-500' : ''}`}
                    onClick={() => toggleAccordion(idx)}
                  >
                    <div className="flex flex-col items-start gap-1 flex-1 text-left">
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold shadow-sm ${
                          entry.action === 'project_created' ? 'bg-green-100 text-green-700' :
                          entry.action === 'project_updated' ? 'bg-blue-100 text-blue-700' :
                          entry.action === 'task_deleted' ? 'bg-red-100 text-red-700' :
                          entry.action === 'worker_assigned' ? 'bg-green-50 text-green-700' :
                          entry.action === 'worker_removed' ? 'bg-red-50 text-red-700' :
                          entry.action === 'offer_sent' ? 'bg-yellow-100 text-yellow-700' :
                          entry.action === 'offer_accepted' ? 'bg-green-100 text-green-700' :
                          entry.action === 'offer_rejected' ? 'bg-red-100 text-red-700' :
                          entry.action === 'counter_offer' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {getActionIcon(entry.action)}
                          <span className="ml-2">{t(`projects.history.${entry.action}`, entry.action)}</span>
                        </span>
                        <span className="text-xs text-gray-500 font-mono ml-2">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <span className="text-xs text-gray-700 mt-1 italic group-hover:text-red-600 transition-colors">{getHistorySummary(entry)}</span>
                    </div>
                    <span className={`ml-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>{isOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </button>
                  {isOpen && (
                    <div className="px-10 pb-8 pt-2 bg-gradient-to-br from-white/90 via-red-50 to-white/80 rounded-b-2xl animate-fadeIn shadow-inner border-t border-red-100">
                      <div className="text-sm text-gray-900 mb-2 flex items-center gap-2">
                        <FaUserCheck className="text-red-300" />
                        <strong>{t('projects.history.performedBy', 'Performed by:')}</strong> {entry.performedBy.email}
                      </div>
                      <div className="text-xs text-gray-700">
                        {renderHistoryData(entry)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredHistory.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📜</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('projects.history.noHistory', 'No history')}</h3>
                <p className="text-gray-500">{t('projects.history.noHistoryMessage', 'No activity records were found for this project.')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectHistory; 